'use client';

import { forwardRef, useImperativeHandle, useState } from 'react';
import { BaseLineClear } from '@/app/components/icons/BaseLineClear';
import { BaseLineSearch } from '@/app/components/icons/BaseLineSearch';
import { Input } from '@/app/components/ui/input';
import { cn } from '@/app/utils';

const MAX_SEARCH_LENGTH = 1500;

interface SearchInputProps {
  defaultValue?: string;
  /** 搜索回调函数 */
  onSearch: (value: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  maxLength?: number;
}

export interface SearchInputRef {
  /** 设置搜索框的值 */
  setValue: (value: string) => void;
}

export const SearchInput = forwardRef<SearchInputRef, SearchInputProps>(
  (
    {
      defaultValue = '',
      onSearch,
      placeholder = 'Search...',
      className = 'w-[176px]',
      inputClassName,
      maxLength = MAX_SEARCH_LENGTH,
    },
    ref,
  ) => {
    // 非受控模式的内部状态
    const [searchText, setSearchText] = useState(defaultValue);
    const [isComposing, setIsComposing] = useState(false);

    // 暴露给父组件的方法
    useImperativeHandle(ref, () => ({
      setValue: (value: string) => {
        setSearchText(value);
        onSearch(value);
      },
    }));

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isComposing && event.key === 'Enter') {
        onSearch(searchText.trim());
      }
    };

    const handleClear = () => {
      setSearchText('');
      onSearch('');
    };

    const handleCompositionStart = () => {
      setIsComposing(true);
    };

    const handleCompositionEnd = () => {
      setIsComposing(false);
    };

    return (
      <div className={cn('relative flex', className)}>
        <Input
          className={cn(
            'peer h-8 w-full min-w-0 truncate rounded-8 border-none bg-system-white10 ps-8 text-sm placeholder:text-system-white36 focus-visible:bg-[#232933] focus-visible:text-system-white',
            searchText ? 'pe-8' : 'pe-4',
            'focus:border-system-white10',
            inputClassName,
          )}
          placeholder={placeholder}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyDown={handleKeyDown}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          maxLength={maxLength}
        />
        <BaseLineSearch
          className="-translate-y-1/2 absolute top-1/2 left-2 cursor-pointer text-base text-system-white opacity-30 peer-focus-visible:text-system-white"
          onClick={() => onSearch(searchText)}
        />
        {searchText && (
          <BaseLineClear
            className="-translate-y-1/2 absolute top-1/2 right-2 cursor-pointer text-base text-system-white opacity-80 transition-opacity hover:opacity-90"
            onClick={handleClear}
          />
        )}
      </div>
    );
  },
);

SearchInput.displayName = 'SearchInput';
